import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { BRAND, NAV_LINKS } from "../lib/constants";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-shadow duration-300 bg-white border-b ${
                scrolled ? "border-black/5 shadow-[0_2px_24px_rgba(0,0,0,0.04)]" : "border-transparent"
            }`}
            data-testid="main-navbar"
        >
            <div className="max-w-7xl mx-auto px-4 md:px-10 h-[60px] md:h-[72px] flex items-center justify-between">
                {/* Logo only — no black box, no text */}
                <a href="#top" className="flex items-center" data-testid="navbar-logo-link" aria-label="Dhanshree Constructions — Best Construction Company in Udaipur">
                    <img
                        src={BRAND.logo}
                        alt="Dhanshree Constructions Udaipur"
                        className="h-8 md:h-10 w-auto object-contain"
                    />
                </a>

                {/* Center nav (desktop) */}
                <nav className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            className="text-[13px] font-medium tracking-wide text-brand-ink/80 hover:text-brand-orange transition-colors"
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                {/* Right CTA */}
                <div className="flex items-center gap-2">
                    <a
                        href={BRAND.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden sm:inline-flex items-center gap-2 text-[13px] font-medium px-4 md:px-5 py-2.5 rounded-full bg-brand-ink text-white hover:bg-brand-orange transition-colors"
                        data-testid="navbar-whatsapp-cta"
                    >
                        <MessageCircle size={14} className="hidden md:inline" />
                        Get a quote
                    </a>
                    <button
                        onClick={() => setOpen(!open)}
                        className="md:hidden w-10 h-10 rounded-full flex items-center justify-center bg-brand-ink text-white"
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className="md:hidden border-t border-black/5 bg-white"
                    >
                        <div className="px-4 py-3">
                            {NAV_LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="block py-3 px-2 text-brand-ink hover:text-brand-orange text-[15px] border-b border-black/5 last:border-0"
                                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href={BRAND.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-3 flex items-center justify-center gap-2 bg-brand-ink text-white py-3 rounded-full font-medium text-sm"
                            >
                                <MessageCircle size={15} />
                                Chat on WhatsApp
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
